/**
 * Hook for loading questionnaire JSON data
 * This handles the static JSON file loading for questionnaire structure
 * NOTE: Uses fetch directly for loading static JSON files (not API calls)
 * This is acceptable as it's loading a static asset, not making API requests
 */

import { useQuery } from '@tanstack/react-query';
import { HttpError } from '../../../shared/httpTypes';

export type Country = 'USA' | 'ES';
export type TaxYear = number;

/**
 * Load questionnaire JSON from file based on country
 * This is a static asset loader, not an API call, so fetch is acceptable here
 */
async function loadQuestionnaireJson(country: Country, year: TaxYear): Promise<any> {
  // Determine file path based on country
  const fileName = country === 'USA' 
    ? '/USA_Tax_Questionnaire_Enhanced_JSON.json'
    : '/SPAIN_Tax_Questionnaire_Decision_Tree_Enhanced.json';
  
  // eslint-disable-next-line no-restricted-syntax
  const response = await fetch(fileName);
  if (!response.ok) {
    throw new HttpError(response.status, response.statusText, `Failed to load ${country} questionnaire`);
  }
  const data = await response.json();
  
  // Add country and year metadata to the data
  return {
    ...data,
    _metadata: {
      country,
      year
    }
  };
}

/**
 * Query key for questionnaire data
 */
export const questionnaireDataQueryKey = (country: Country, year: TaxYear) => 
  ['questionnaire', 'data', country, year] as const;

/**
 * Hook for loading questionnaire JSON data
 * @param country - Country code ('USA' or 'ES')
 * @param year - Tax year
 * @param enabled - Whether to enable the query (default: true)
 */
export function useLoadQuestionnaire(
  country: Country | null = null,
  year: TaxYear | null = null,
  enabled: boolean = true
) {
  return useQuery<any, HttpError>({
    queryKey: country && year ? questionnaireDataQueryKey(country, year) : ['questionnaire', 'data'],
    queryFn: () => {
      if (!country || !year) {
        throw new Error('Country and year are required');
      }
      return loadQuestionnaireJson(country, year);
    },
    enabled: enabled && country !== null && year !== null,
    staleTime: Infinity, // JSON data doesn't change, cache forever
    gcTime: Infinity,
  });
}

